import { create } from '@bufbuild/protobuf'
import { InfoIcon, LayoutGridIcon, MessagesSquareIcon, StampIcon, Trash2Icon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useListAlerts } from '@/api/alerts.hook'
import { useListCompanyArtifact, useListCompanyArtifactCounts } from '@/api/artifact.hook'
import { useGetOngoingAssessment } from '@/api/assessments.hook'
import { useListAssessorQuestionnaires } from '@/api/assessor-questionnaire.hook'
import { useGetCompany } from '@/api/company.hook'
import { REFETCH_INTERVAL_MS } from '@/api/consts'
import { useListControls } from '@/api/control.hook'
import { useIntegration } from '@/api/get-integrations.hook'
import { AssessmentScore } from '@/gen/assessment/v1/assessment_score_pb'
import { ListAlertsRequestSchema } from '@/gen/inventory/v1/alert_service_pb'
import { CompanyStatus } from '@/gen/inventory/v1/company_status_pb'
import { ListControlsRequestSchema } from '@/gen/inventory/v1/control_service_pb'
import { IntegrationStatus } from '@/gen/inventory/v1/integration_service_pb'
import { OngoingAssessmentStatus } from '@/gen/inventory/v1/ongoing_assessment_pb'
import { QuestionnaireType } from '@/gen/questionnaire/v2/model_pb'

import { SECTION_LABELS } from '@/const/label'

import { useTrackCallback } from '@/lib/analytics/events'
import { useFeatureFlagEnabled } from '@/lib/config/featureflag'
import { isUnvalidatedControlStatus } from '@/lib/control-status'
import { formatTimestamp } from '@/lib/date/date'
import { cn } from '@/lib/ui/style-helpers'
import { UrlParam, useUrlParams } from '@/lib/url/url-param.hook'
import { buildPastAssessmentUrl, buildThirdPartyUrl } from '@/lib/url/urls'

import { countOpenOrOngoingAlerts } from '@/pages/alert-inventory/utils'
import { ArtifactsTab } from '@/pages/assessment/assessment-tabs/artifacts-tab'
import { OverviewTab } from '@/pages/assessment/assessment-tabs/assessment-overview-tab/overview-tab'
import { ASSESSMENT_TABS } from '@/pages/assessment/assessment-tabs/assessment-tabs'
import { ControlsTab } from '@/pages/assessment/assessment-tabs/controls-tab'
import { IssuesTab } from '@/pages/assessment/assessment-tabs/issues-tab'
import { QuestionnairesTab } from '@/pages/assessment/assessment-tabs/questionnaires-tab'
import { AssessmentBreadcrumbSwitcher } from '@/pages/assessment/components/assessment-breadcrumb-switcher'
import { AssessmentCommentsPanel } from '@/pages/assessment/components/assessment-comments/assessment-comments-panel'
import { commentsTabLabel } from '@/pages/assessment/components/assessment-comments/labels'
import { useUnreadAssessmentComments } from '@/pages/assessment/components/assessment-comments/use-unread-assessment-comments'
import { AssigneeDisplay } from '@/pages/assessment/components/assignee-display'
import { DeleteAssessmentModal } from '@/pages/assessment/components/delete-assessment-modal/delete-assessment-modal'
import { JiraAssessmentIntegration } from '@/pages/assessment/components/jira-assessment-integration/jira-assessment-integration'
import { RailHeaderRow } from '@/pages/assessment/components/rail-header-row'
import { RightSideCompanyInfo } from '@/pages/assessment/components/right-side-company-info'
import { StatCard } from '@/pages/assessment/components/stat-card'
import { useSuggestToRejectCompanyModal } from '@/pages/assessment/components/suggest-to-reject-company-modal/use-suggest-to-reject-company-modal.hook'
import { CompanyDrawerTabs } from '@/pages/company-drawer/company-drawer-tabs-enum'

import { Empty } from '@/components/lemmala-design-system/empty/empty'
import { Loading } from '@/components/lemmala-design-system/loading/loading'
import { SegmentedControlGroup } from '@/components/lemmala-design-system/segmented-controls/segmented-control-group'
import { Separator } from '@/components/lemmala-design-system/separator/separator'
import { StatisticsColumn } from '@/components/lemmala-design-system/statistics/stats-column'
import { TagsList } from '@/components/lemmala-design-system/tag/tag'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'
import { AssessmentUserRow } from '@/components/shared/assessment-user-row/assessment-user-row'
import { COMMENT_EVENT_SOURCES } from '@/components/shared/comment/consts'
import { TenantCommentsProvider } from '@/components/shared/comment/tenant-comments-provider'
import { OngoingAssessmentStatusBadge } from '@/components/shared/ongoing-assessment-status-badge/ongoing-assessment-status-badge'
import { TabbedPageLayout } from '@/components/shared/tabbed-page-layout/tabbed-page-layout'

import {
  type AssessmentAnalyticsContextWithProject,
  AssessmentAnalyticsProvider,
  buildAssessmentAnalyticsContext,
} from './analytics'
import { useAssessmentCtx } from './assessment-context'
import { AssessmentProvider } from './assessment-provider'
import { ConclusionTab } from './assessment-tabs/conclusion/conclusion-tab'
import { ASSESSMENT_PAGE_LABELS } from './labels'

const SingleAssessmentContent = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>()

  const { ongoingAssessment, isLoading } = useGetOngoingAssessment(assessmentId)
  const { openDeleteModal } = useAssessmentCtx()

  const companyId = ongoingAssessment?.orgCompanyId ?? ''
  const projectId = ongoingAssessment?.projectId || undefined
  const { urlParams } = useUrlParams()
  const currentTabRef = useRef(urlParams[UrlParam.ASSESSMENT_TAB])
  const trackedAssessmentViewRef = useRef<string | null>(null)

  const navigate = useNavigate()
  const trackAssessmentView = useTrackCallback('third-party.assessment.view')
  const trackTopNavigationClick = useTrackCallback('third-party.assessment.top.navigation.click')
  const trackTabView = useTrackCallback('third-party.assessment.tab.view')

  const navigateToInventory = useCallback(() => {
    navigate(
      buildThirdPartyUrl(companyId, {
        [UrlParam.COMPANY_DRAWER_TAB]: CompanyDrawerTabs.ASSESSMENT,
      }),
    )
  }, [navigate, companyId])

  const {
    showModal: showSuggestToRejectCompanyModal,
    renderModal: renderSuggestToRejectCompanyModal,
  } = useSuggestToRejectCompanyModal(companyId, navigateToInventory)

  const handleFinishAssessment = useCallback(
    (hasRejectedAssessment: boolean, isFinalAssessment: boolean, pastAssessmentId?: string) => {
      // Show rejection confirmation modal if needed
      // Navigate after modal completes via onComplete callback
      if (isFinalAssessment && hasRejectedAssessment) {
        showSuggestToRejectCompanyModal()
        return
      }

      if (pastAssessmentId) {
        navigate(buildPastAssessmentUrl(pastAssessmentId))
      } else {
        navigateToInventory()
      }
    },
    [navigateToInventory, showSuggestToRejectCompanyModal, navigate],
  )

  const { data: company, isLoading: isCompanyLoading } = useGetCompany(companyId)
  const { data: controls, isLoading: isControlsLoading } = useListControls({
    ...create(ListControlsRequestSchema, { assessmentId: assessmentId ?? '' }),
    enabled: !!assessmentId,
  })
  // One artifact query at a time: while the tab is open, its list response carries the counts.
  const isArtifactsTabActive = urlParams[UrlParam.ASSESSMENT_TAB] === ASSESSMENT_TABS.ARTIFACTS
  const { data: artifactCounts, isLoading: isArtifactCountsLoading } = useListCompanyArtifactCounts(
    companyId,
    !isArtifactsTabActive,
  )
  const { data: artifactList, isLoading: isArtifactListLoading } = useListCompanyArtifact(
    companyId,
    isArtifactsTabActive,
  )
  const artifactBadgeCounts = isArtifactsTabActive
    ? artifactList?.totalCounts
    : artifactCounts?.totalCounts
  const isArtifactBadgeLoading = isArtifactsTabActive
    ? isArtifactListLoading
    : isArtifactCountsLoading
  const { data: questionnaires, isLoading: isQuestionnairesLoading } =
    useListAssessorQuestionnaires({
      companyId,
      ongoingAssessmentId: assessmentId,
      refetchInterval: REFETCH_INTERVAL_MS,
    })

  const { data: alertsData, isLoading: isAlertsLoading } = useListAlerts(
    create(ListAlertsRequestSchema, { companyId }),
    { enabled: !!companyId },
  )
  const alerts = alertsData.alerts
  const openAlertsCount = useMemo(() => countOpenOrOngoingAlerts(alerts), [alerts])

  const { isEnabled: isQuestionnaireModuleEnabled } =
    useFeatureFlagEnabled('questionnaire-module-v2')

  // Find smart evidence request questionnaire (RISK type)
  const smartEvidenceQuestionnaire = useMemo(() => {
    return questionnaires?.questionnaires?.find((q) => q.base?.type === QuestionnaireType.RISK)
      ?.base
  }, [questionnaires])

  const enabledControls = useMemo(
    () => controls?.filter((control) => control.isEnabled) ?? [],
    [controls],
  )
  const uniqControlScopeNames = useMemo(
    () => [...new Set(enabledControls.map((control) => control.scope?.name))],
    [enabledControls],
  )
  const unvalidatedControls = useMemo(
    () => enabledControls.filter((control) => isUnvalidatedControlStatus(control.status)),
    [enabledControls],
  )

  const companyData = company?.company
  const analyticsContext = useMemo<AssessmentAnalyticsContextWithProject | undefined>(() => {
    if (!ongoingAssessment || !companyData) {
      return undefined
    }

    return buildAssessmentAnalyticsContext({
      ongoingAssessment,
      company: companyData,
    })
  }, [companyData, ongoingAssessment])

  useEffect(() => {
    currentTabRef.current = urlParams[UrlParam.ASSESSMENT_TAB]
  }, [urlParams])

  useEffect(() => {
    if (!analyticsContext) {
      return
    }

    if (trackedAssessmentViewRef.current === analyticsContext.assessmentId) {
      return
    }

    trackedAssessmentViewRef.current = analyticsContext.assessmentId
    trackAssessmentView(analyticsContext)
  }, [analyticsContext, trackAssessmentView])

  const handleBreadcrumbItemClick = useCallback(
    (item: { value?: string }, index: number) => {
      if (!analyticsContext || index !== 0 || item.value !== 'assessments') {
        return
      }

      trackTopNavigationClick({
        ...analyticsContext,
        fromTab: currentTabRef.current,
      })
    },
    [analyticsContext, trackTopNavigationClick],
  )

  const handleTabChange = useCallback(
    (tab: string) => {
      if (!analyticsContext) {
        currentTabRef.current = tab
        return
      }

      const previousTab = currentTabRef.current
      const nextTab = tab
      currentTabRef.current = nextTab

      trackTabView({
        ...analyticsContext,
        tab: nextTab,
        previousTab,
      })
    },
    [analyticsContext, trackTabView],
  )

  if (
    isLoading ||
    isCompanyLoading ||
    isQuestionnairesLoading ||
    isControlsLoading ||
    isAlertsLoading
  ) {
    return <Loading />
  }
  if (!ongoingAssessment || !companyData) {
    return (
      <Empty
        icon='not-found'
        title='Assessment not found'
        subtitle='The assessment you are looking for does not exist or has been deleted.'
        className='h-full'
      />
    )
  }

  return (
    <AssessmentAnalyticsProvider value={analyticsContext}>
      <TabbedPageLayout
        rightSideContent={
          <RightSideContent companyId={companyId} assessmentId={ongoingAssessment.id} />
        }
        tabUrlParamName={UrlParam.ASSESSMENT_TAB}
        onTabChange={handleTabChange}
        menuItems={[
          {
            content: (
              <TextWithIcon
                className='text-main-foreground-primary text-body-default'
                text='Delete assessment'
                icon={<Trash2Icon className='size-4' />}
              />
            ),
            disabledTooltip: 'Delete assessment',
            onClick: () => {
              openDeleteModal({ ongoingAssessmentId: assessmentId || '', companyId })
            },
          },
        ]}
        breadcrumbs={[
          { label: SECTION_LABELS.assessments, value: 'assessments', to: '/assessments' },
          {
            label: ongoingAssessment?.orgCompanyName,
            value: ongoingAssessment?.orgCompanyId,
            to: buildThirdPartyUrl(
              ongoingAssessment?.orgCompanyId,
              {
                [UrlParam.COMPANY_DRAWER_TAB]: CompanyDrawerTabs.ASSESSMENT,
              },
              { absolute: true },
            ),
          },
          {
            label: (
              <AssessmentBreadcrumbSwitcher
                companyId={companyId}
                currentAssessmentId={assessmentId || ''}
                ongoingAssessment={ongoingAssessment}
              />
            ),
            value: assessmentId,
          },
        ]}
        onBreadcrumbItemClick={handleBreadcrumbItemClick}
        tabItems={[
          {
            label: ASSESSMENT_TABS.OVERVIEW,
            component: (
              <OverviewTab
                projectId={projectId}
                assessmentScore={ongoingAssessment?.score || AssessmentScore.UNSPECIFIED}
                alerts={alerts}
                companyId={companyId}
                companyStatus={companyData.status || CompanyStatus.UNSPECIFIED}
                engagementModeEnabled={!!companyData.engagementMode}
                controls={enabledControls || []}
                artifactCounts={artifactBadgeCounts}
                artifactCountsLoading={isArtifactBadgeLoading}
                questionnaires={questionnaires?.questionnaires || []}
              />
            ),
            titleIcon: <LayoutGridIcon className='size-4' />,
          },
          {
            label: ASSESSMENT_TABS.ISSUES,
            tabTitle: (
              <div className='flex items-center justify-between gap-2 py-4'>
                <div className='text-heading-medium'>Issues</div>
                <div className='flex items-center'>
                  <div className='flex gap-6'>
                    <StatCard
                      count={unvalidatedControls.length}
                      label='Unvalidated controls'
                      variant='danger'
                    />
                    {openAlertsCount > 0 && (
                      <StatCard
                        count={openAlertsCount}
                        label={SECTION_LABELS.alerts}
                        variant='danger'
                      />
                    )}
                  </div>
                </div>
              </div>
            ),
            component: (
              <IssuesTab
                assessmentId={assessmentId || ''}
                company={companyData}
                unvalidatedControls={unvalidatedControls}
                alerts={alerts}
                smartEvidenceQuestionnaire={smartEvidenceQuestionnaire}
                dueDate={ongoingAssessment?.dueDate}
              />
            ),
            badge: unvalidatedControls.length,
          },
          {
            label: ASSESSMENT_TABS.ARTIFACTS,
            component: (
              <ArtifactsTab companyId={companyId} companyName={companyData.profile?.name || ''} />
            ),
            badge: artifactBadgeCounts?.nonArchived,
            isLoading: isArtifactBadgeLoading,
          },
          ...(isQuestionnaireModuleEnabled
            ? [
                {
                  label: ASSESSMENT_TABS.QUESTIONNAIRES,
                  component: (
                    <QuestionnairesTab
                      company={companyData}
                      questionnaires={questionnaires?.questionnaires || []}
                      isLoading={isQuestionnairesLoading}
                      assessmentId={assessmentId || ''}
                      dueDate={ongoingAssessment?.dueDate}
                    />
                  ),
                  badge:
                    questionnaires?.questionnaires?.filter(
                      (q) => q.base?.type !== QuestionnaireType.RISK,
                    ).length || 0,
                },
              ]
            : []),
          {
            tabTitle: (
              <div className='flex items-center gap-2 py-6'>
                <div className='text-heading-medium'>{ASSESSMENT_TABS.CONTROLS}</div>
                <TagsList
                  maxDisplay={10}
                  tags={uniqControlScopeNames.map((name) => ({ id: name ?? '', name: name ?? '' }))}
                />
              </div>
            ),
            label: ASSESSMENT_TABS.CONTROLS,
            component: (
              <ControlsTab
                assessmentId={assessmentId || ''}
                controls={enabledControls}
                isLoading={isControlsLoading}
                companyId={companyId}
                projectId={projectId}
              />
            ),
            badge: enabledControls.length,
          },
          {
            label: ASSESSMENT_TABS.CONCLUSION,
            component: (
              <ConclusionTab
                assessmentId={assessmentId || ''}
                readonly={false}
                onFinishAssessment={handleFinishAssessment}
              />
            ),
            titleIcon: <StampIcon className='size-4' />,
          },
        ]}
      />
      <DeleteAssessmentModal redirectOnDelete />
      {renderSuggestToRejectCompanyModal()}
    </AssessmentAnalyticsProvider>
  )
}

export const SingleAssessment = () => {
  return (
    <AssessmentProvider mode='all'>
      <TenantCommentsProvider>
        <SingleAssessmentContent />
      </TenantCommentsProvider>
    </AssessmentProvider>
  )
}

type RightSideContentProps = {
  companyId: string
  assessmentId: string
}

const RAIL_VIEWS = {
  COMMENTS: 'comments',
  DETAILS: 'details',
} as const

type RailView = (typeof RAIL_VIEWS)[keyof typeof RAIL_VIEWS]

const RightSideContent = ({ companyId, assessmentId }: RightSideContentProps) => {
  const [railView, setRailView] = useState<RailView>(RAIL_VIEWS.DETAILS)
  const [isCommentsMounted, setIsCommentsMounted] = useState(false)
  const trackCommentsPanelOpened = useTrackCallback('assessment.comment.panel.opened')
  const { hasUnread, markAllAsRead } = useUnreadAssessmentComments(assessmentId)
  const showComments = railView === RAIL_VIEWS.COMMENTS

  const selectRailView = (view: RailView) => {
    setRailView(view)
    if (view === RAIL_VIEWS.COMMENTS) {
      setIsCommentsMounted(true)
      markAllAsRead()
      trackCommentsPanelOpened({
        assessment_id: assessmentId,
        company_id: companyId,
        source: COMMENT_EVENT_SOURCES.ASSESSMENT_PANEL,
      })
    }
  }

  return (
    <div className='flex h-full min-h-0 flex-col bg-main-background-canvas'>
      <div className='flex shrink-0 items-center justify-end pl-6 pr-4 pt-3'>
        <SegmentedControlGroup<RailView>
          segments={[
            {
              value: RAIL_VIEWS.COMMENTS,
              'aria-label': commentsTabLabel(hasUnread),
              icon: (
                <span className='relative inline-flex'>
                  <MessagesSquareIcon className='size-4' />
                  {hasUnread && (
                    <span
                      className='absolute -right-1 -top-0.5 size-2.5 rounded-full bg-main-background-accent'
                      aria-hidden
                    />
                  )}
                </span>
              ),
            },
            {
              value: RAIL_VIEWS.DETAILS,
              'aria-label': ASSESSMENT_PAGE_LABELS.assessmentDetails,
              icon: <InfoIcon className='size-4' />,
            },
          ]}
          selectedValue={railView}
          onSelectionChange={selectRailView}
        />
      </div>

      {isCommentsMounted && (
        <div className={cn('flex min-h-0 flex-1 flex-col', !showComments && 'hidden')}>
          <AssessmentCommentsPanel assessmentId={assessmentId} />
        </div>
      )}

      <div className={cn('min-h-0 flex-1 overflow-auto', showComments && 'hidden')}>
        <RightSideAssessmentInfo assessmentId={assessmentId} companyId={companyId} />
        <RightSideCompanyInfo companyId={companyId} />
        <Separator className='mt-4' />
      </div>
    </div>
  )
}

type RightSideAssessmentInfoProps = {
  assessmentId: string
  companyId: string
}

const RightSideAssessmentInfo = ({ assessmentId, companyId }: RightSideAssessmentInfoProps) => {
  const { isLoading: isLoadingAssessment, ongoingAssessment } =
    useGetOngoingAssessment(assessmentId)
  const { data: jiraIntegration } = useIntegration({
    integrationId: 'jira',
  })
  const jiraIntegrationEnabled = jiraIntegration?.status === IntegrationStatus.ADDED
  if (isLoadingAssessment) {
    return <Loading />
  }

  return (
    <div className='flex flex-col gap-2 border-b border-main-border-default px-6 pb-6 pt-3'>
      <RailHeaderRow>
        <div className='text-main-foreground-primary text-label-default'>
          {ASSESSMENT_PAGE_LABELS.assessmentDetails}
        </div>
      </RailHeaderRow>
      <StatisticsColumn
        items={[
          {
            title: 'Created by',
            hide: !(ongoingAssessment?.createdBy?.name || ongoingAssessment?.createdBy?.email),
            children: <AssessmentUserRow users={[ongoingAssessment?.createdBy]} />,
          },
          {
            title: 'Status',
            hide: !ongoingAssessment?.status,
            children: (
              <OngoingAssessmentStatusBadge
                status={ongoingAssessment?.status ?? OngoingAssessmentStatus.UNSPECIFIED}
              />
            ),
          },
          {
            title: 'Project',
            hide: !ongoingAssessment?.projectName,
            children: <div className='text-body-small'>{ongoingAssessment?.projectName}</div>,
          },
          {
            title: 'Jira ticket',
            hide: !jiraIntegrationEnabled,
            children: (
              <JiraAssessmentIntegration assessmentId={assessmentId} companyId={companyId} />
            ),
          },
          {
            title: 'Assignees',
            children: (
              <AssigneeDisplay
                assignees={ongoingAssessment?.assignees || []}
                ongoingAssessmentId={assessmentId}
                companyId={ongoingAssessment?.orgCompanyId}
              />
            ),
          },
          {
            title: 'Concluded by',
            hide: !(ongoingAssessment?.concludedBy?.name || ongoingAssessment?.concludedBy?.email),
            children: <AssessmentUserRow users={[ongoingAssessment?.concludedBy]} />,
          },
          {
            title: 'Start date',
            hide: !ongoingAssessment?.createdAt,
            children: (
              <div className='text-body-small'>{formatTimestamp(ongoingAssessment?.createdAt)}</div>
            ),
          },
        ]}
      />
    </div>
  )
}
